import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (path) => {
    setIsMobileMenuOpen(false);
    if (path.includes('#')) {
      const [pathname, hash] = path.split('#');
      if (pathname === location.pathname || pathname === '/') {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  };

  const navLinks = [
    { name: 'Hjem', path: '/' },
    { name: 'Vores løsning', path: '/solutions' },
    { name: 'Festivaler', path: '/festivals' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background/95 backdrop-blur-md shadow-soft' : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img 
              src="/museiq-logo.png" 
              alt="MUSEIQ" 
              className="h-12 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className={`text-sm font-medium transition-colors hover:text-accent-blue ${
                  location.pathname === link.path || 
                  (link.path.includes('#about') && location.pathname === '/')
                    ? 'text-accent-blue'
                    : 'text-body-text'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Button
              asChild
              variant={location.pathname === '/festival-kalender' ? 'default' : 'outline'}
              className={`rounded-full px-5 ${
                location.pathname === '/festival-kalender'
                  ? 'bg-accent-blue text-white hover:bg-accent-blue/90'
                  : 'border-primary/20 bg-white/80 text-primary hover:bg-white'
              }`}
            >
              <Link to="/festival-kalender">Festivalkalender</Link>
            </Button>
            <Button 
              className="bg-green-600 text-white transition-all hover:bg-green-700 hover:shadow-medium"
              onClick={() => {
                const subject = encodeURIComponent("Anmodning om demo - MUSEIQ");
                const body = encodeURIComponent("Hej MuseIQ\n\nJeg vil gerne booke en demo af jeres løsning.\n\nI må meget gerne kontakte mig for at aftale et tidspunkt.\n\nBedste hilsner\n[Navn]\n[Virksomhed]");
                window.location.href = `mailto:Kasper@museiq.io?subject=${subject}&body=${body}`;
              }}
            >
              Book en demo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-primary"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border bg-background">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-sm font-medium text-body-text hover:text-accent-blue transition-colors"
                  onClick={() => handleNavClick(link.path)}
                >
                  {link.name}
                </Link>
              ))}
              <Button
                asChild
                variant={location.pathname === '/festival-kalender' ? 'default' : 'outline'}
                className={`w-full ${
                  location.pathname === '/festival-kalender'
                    ? 'bg-accent-blue text-white hover:bg-accent-blue/90'
                    : 'border-primary/20 text-primary'
                }`}
              >
                <Link to="/festival-kalender" onClick={() => setIsMobileMenuOpen(false)}>
                  Festivalkalender
                </Link>
              </Button>
              <Button 
                className="w-full bg-green-600 text-white hover:bg-green-700"
                onClick={() => {
                  const subject = encodeURIComponent("Anmodning om demo - MUSEIQ");
                  const body = encodeURIComponent("Hej MuseIQ\n\nJeg vil gerne booke en demo af jeres løsning.\n\nI må meget gerne kontakte mig for at aftale et tidspunkt.\n\nBedste hilsner\n[Navn]\n[Virksomhed]");
                  window.location.href = `mailto:Kasper@museiq.io?subject=${subject}&body=${body}`;
                }}
              >
                Book en demo
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
